import { isSet } from '@core/util/value';
import { DATE_FORMAT } from '@models/businesses';
import { AdvancedAmountLabels } from '@models/llc-mortgages';
import {
	MatterMortgagePriority,
	NetAdvancedAmount,
	NetAmountType,
} from '@models/mortgage';
import {
	CloserSyncInstruction,
	SyncCreditFee,
	SyncFee,
} from '@models/mortgages/closer-sync-instruction';
import { format, parseISO } from 'date-fns';
import { createId } from 'src/app/shared/core/util/file';

export function getDate(date: string) {
	return date ? format(parseISO(date), DATE_FORMAT)?.toString() : null;
}

/**
 * Converts a priority number to a MatterMortgagePriority enum value.
 * @param priority - The priority number.
 * @returns The corresponding MatterMortgagePriority enum value.
 */
export function getPriority(priority: number | null): MatterMortgagePriority {
	switch (priority) {
		case 1:
			return MatterMortgagePriority.PRIORITY_1;
		case 2:
			return MatterMortgagePriority.PRIORITY_2;
		case 3:
			return MatterMortgagePriority.PRIORITY_3;
		case 4:
			return MatterMortgagePriority.PRIORITY_4;
		case 5:
			return MatterMortgagePriority.PRIORITY_5;
		case null:
			return null;
		default:
			return MatterMortgagePriority.PRIORITY_1;
	}
}

export function parseInboundInstructionDto(
	instruction: CloserSyncInstruction,
): CloserSyncInstruction {
	// handle backwards compatibility for credits being one or many...
	instruction.mortgagePayment.fees.credits = Array.isArray(
		instruction.mortgagePayment.fees.credits,
	)
		? instruction.mortgagePayment.fees.credits
		: instruction.mortgagePayment.fees.credits !== undefined
			? [instruction.mortgagePayment.fees.credits]
			: [{} as SyncCreditFee];

	return instruction;
}

/**
 * Builds an array of NetAdvancedAmount objects based on the transaction data's mortgage fees.
 * @param instruction - The transaction data.
 * @returns An array of NetAdvancedAmount objects representing the mortgage fees.
 */
export function buildMortgageFees(
	instruction: CloserSyncInstruction,
): NetAdvancedAmount[] {
	const fees = instruction?.mortgagePayment?.fees;
	const advances: NetAdvancedAmount[] = [];
	populateLegacyFees(instruction, advances, fees);
	populateDynamicFees(instruction, advances);

	return advances;
}

function buildAdvances(
	fees: SyncFee[],
	isCredit: boolean,
): NetAdvancedAmount[] {
	let baseType: NetAmountType | undefined;

	if (isCredit) {
		baseType = NetAmountType.OTHER_CREDIT;
	}

	const advances = fees
		.filter((fee) => !!fee.value)
		.map((fee) => ({
			id: createId(),
			amount: treatDollarsAsCents(fee.value),
			description: fee.description || fee.name,
			type: baseType ?? (fee.name as NetAmountType),
			calculated: true,
		}));

	return advances;
}

function populateDynamicFees(
	instruction: CloserSyncInstruction,
	advances: NetAdvancedAmount[],
) {
	if (Array.isArray(instruction?.mortgagePayment?.fees?.credits)) {
		const creditAdvances = buildAdvances(
			instruction.mortgagePayment.fees.credits,
			true,
		);
		advances.push(...creditAdvances);
	}

	if (Array.isArray(instruction?.mortgagePayment?.fees?.debits)) {
		const debitAdvances = buildAdvances(
			instruction.mortgagePayment.fees.debits,
			false,
		);
		advances.push(...debitAdvances);
	}
}

function treatDollarsAsCents(num: number): number {
	return num * 100;
}

function populateLegacyFees(
	instruction: CloserSyncInstruction,
	advances: NetAdvancedAmount[],
	fees: {
		appraisalFee: number;
		brokerFee: number;
		defaultInsuranceAmount: number;
		defaultInsuranceTaxAmount: number;
		defaultInsuranceApplicationFee: number;
		eftFee: number;
		holdback: number;
		interestAdjustment: number;
		lenderFee: number;
		miscellaneousFee: number;
		propertyTaxHoldback: number;
		wireFee: number;
		debits: SyncFee[];
		credits: SyncCreditFee[] | { cashBackAmount: number };
	},
) {
	const feeMappings = [
		{
			key: 'wireFee',
			label: AdvancedAmountLabels.WIRE_FEE,
			type: NetAmountType.WIRE_FEE,
		},
		{ key: 'brokerFee', label: 'Broker Fee', type: NetAmountType.OTHER },
		{
			key: 'interestAdjustment',
			label: 'Interest Adjustment',
			type: NetAmountType.OTHER,
		},
		{
			key: 'lenderFee',
			label: AdvancedAmountLabels.LENDER_FEE,
			type: NetAmountType.LENDER_FEE,
		},
		{
			key: 'appraisalFee',
			label: AdvancedAmountLabels.APPRAISAL_FEE,
			type: NetAmountType.APPRAISAL_FEE,
		},
		{
			key: 'holdback',
			label: AdvancedAmountLabels.HOLD_BACK_FEE,
			type: NetAmountType.HOLDBACK_FEE,
		},
		{
			key: 'defaultInsuranceApplicationFee',
			label: AdvancedAmountLabels.INSURANCE_APP_FEE,
			type: NetAmountType.INSURANCE_APPLICATION_FEE,
		},
		{
			key: 'defaultInsuranceAmount',
			label: AdvancedAmountLabels.INSURANCE_PREMIUM,
			type: NetAmountType.INSURANCE_PREMIUM,
		},
		{
			key: 'miscellaneousFee',
			label: AdvancedAmountLabels.MISC_FEE,
			type: NetAmountType.MISC_FEE,
		},
		{
			key: 'defaultInsuranceTaxAmount',
			label: AdvancedAmountLabels.TAX_INSURANCE_PREMIUM,
			type: NetAmountType.TAX_INS_PREM,
		},
		{
			key: 'insurancePortion',
			label: 'Insurance Portion',
			type: NetAmountType.OTHER,
		},
		{ key: 'eftFee', label: 'EFT Fee', type: NetAmountType.OTHER },
	];

	if (isSet(instruction?.mortgagePayment?.retainedByLenderAmount)) {
		advances.push({
			id: createId(),
			amount: instruction.mortgagePayment.retainedByLenderAmount,
			description: 'Withheld by Lender',
			type: NetAmountType.OTHER,
			calculated: true,
		});
	}

	for (const mapping of feeMappings) {
		if (fees?.[mapping.key]) {
			advances.push({
				id: createId(),
				amount: fees[mapping.key],
				description: mapping.label,
				type: mapping.type,
				calculated: true,
			});
		}
	}

	const isCreditsArray = Array.isArray(fees?.credits);
	if (isCreditsArray) {
		const credits = fees?.credits as SyncCreditFee[];
		if (credits.some((credit) => !!credit.cashBackAmount)) {
			advances.push(
				...(credits
					?.filter((credit) => !!credit.cashBackAmount)
					?.map((credit) => ({
						id: createId(),
						amount: credit.cashBackAmount,
						description: 'Cashback',
						type: NetAmountType.OTHER_CREDIT,
						calculated: true,
					})) || []),
			);
		}
		if (credits.some((credit) => !!credit.prepaymentRefund)) {
			advances.push(
				...(credits
					?.filter((credit) => !!credit.prepaymentRefund)
					?.map((credit) => ({
						id: createId(),
						amount: credit.prepaymentRefund,
						description: 'Prepayment refund',
						type: NetAmountType.OTHER_CREDIT,
						calculated: true,
					})) || []),
			);
		}
	} else {
		const credit = fees?.credits as {
			prepaymentRefund: number;
			cashBackAmount: number;
		};
		if (credit?.cashBackAmount) {
			advances.push({
				id: createId(),
				amount: credit.cashBackAmount,
				description: 'Cashback',
				type: NetAmountType.OTHER_CREDIT,
				calculated: true,
			});
		}
		if (credit?.prepaymentRefund) {
			advances.push({
				id: createId(),
				amount: credit.prepaymentRefund,
				description: 'Prepayment refund',
				type: NetAmountType.OTHER_CREDIT,
				calculated: true,
			});
		}
	}
}
